import { LoadingButton } from '@mui/lab';
import { Box, Button, Grid } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import GeneralUpload from 'src/components/general/GeneralUpload';
import { FormProvider, RHFTextField } from 'src/components/hook-form';
import { MEDIA_GROUP_PLAYLIST } from 'src/constants/queryKeys';
import { networkCreateMediaGroup, networkUpdateMediaGroup, useFetchMediaGroupTypeList } from 'src/network/media-group';
import useFormsModalStore from 'src/store/forms';
import { generateSelectMenuItems } from 'src/utils';
import { getDefaultValues } from './defaultValues';
import { GeneralSelectMenu } from 'src/components/general';

const MediaGroupForm = () => {
  const { modal, closeModal } = useFormsModalStore();
  const isEditMode = modal?.data;

  const queryClient = useQueryClient();
  const { mediaGroupTypeId } = useParams();

  const { enqueueSnackbar } = useSnackbar();

  const [mediaGroupTypeList, setMediaGroupTypeList] = useState([]);
  const [selectedMediaGroupType, setSelectedMediaGroupType] = useState('');

  const defaultValues = getDefaultValues(modal.data);

  const methods = useForm({
    // resolver: yupResolver(schema), // todo schema
    defaultValues,
  });

  const {
    reset,
    setValue,
    trigger,
    handleSubmit,
    formState: { isSubmitting },
  } = methods;

  const onSubmit = async (values, event) => {
    const PAYLOAD = {
      ...values,
      MediaGroupReferenceID: '',
      MediaGroupMediaGroupTypeID: mediaGroupTypeId === '-1' ? selectedMediaGroupType : +mediaGroupTypeId,
    };

    try {
      if (isEditMode) {
        const res = await networkUpdateMediaGroup(modal?.data?.MediaGroupID, PAYLOAD);
        if (res.data?.items?.ResultID !== -1) {
          enqueueSnackbar('پلی لیست  موردنظر با موفقیت ویرایش شد');
          if (event.nativeEvent.submitter.name === 'save') {
            closeModal();
            reset();
            await queryClient.invalidateQueries({ queryKey: [MEDIA_GROUP_PLAYLIST] });
          }
        } else {
          enqueueSnackbar('با مشکل مواجه شد !', { variant: 'error' });
        }
      } else {
        const res = await networkCreateMediaGroup(PAYLOAD);
        if (res.data?.items?.ResultID !== -1) {
          enqueueSnackbar('پلی لیست  موردنظر با موفقیت ایجاد شد');
          if (event.nativeEvent.submitter.name === 'save') {
            closeModal();
          }
          reset();
          await queryClient.invalidateQueries({ queryKey: [MEDIA_GROUP_PLAYLIST] });
        } else {
          enqueueSnackbar('با مشکل مواجه شد !', { variant: 'error' });
        }
      }
    } catch (error) {
      console.error(error);
    }
  };

  const { data: mediaGroupTypeResult } = useFetchMediaGroupTypeList();

  useEffect(() => {
    if (mediaGroupTypeResult) {
      const mediaGroupTypes = generateSelectMenuItems(mediaGroupTypeResult?.data?.items, 'MediaGroupTypeLable', 'MediaGroupTypeID');
      setMediaGroupTypeList(mediaGroupTypes);
      setSelectedMediaGroupType(modal.data?.MediaGroupTypeID || mediaGroupTypes?.[0]?.id);
    }
  }, [mediaGroupTypeResult]);

  return (
    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Box sx={{ p: 3 }}>
            <Box
              sx={{
                display: 'grid',
                columnGap: 2,
                rowGap: 3,
                gridTemplateColumns: { xs: 'repeat(1, 1fr)', sm: 'repeat(2, 1fr)' },
              }}
            >
              <RHFTextField name="MediaGroupTitle" type="text" label="عنوان" />

              <RHFTextField name="MediaGroupSubTitle" type="text" label="زیر عنوان" />

              {mediaGroupTypeId === '-1' && (
                <GeneralSelectMenu
                  label="نوع گروه بندی"
                  name="MediaGroupMediaGroupTypeID"
                  options={mediaGroupTypeList}
                  value={selectedMediaGroupType}
                  onChangeValue={setSelectedMediaGroupType}
                />
              )}

              <GeneralUpload
                label="تصویر"
                id="media_group_image"
                name="MediaGroupImage"
                afterChangeAction={(res) => {
                  setValue('MediaGroupImage', res);
                  trigger('MediaGroupImage');
                }}
              />
              <GeneralUpload
                label="پس زمینه"
                id="media_group_backdroup"
                name="MediaGroupBackDrop"
                afterChangeAction={(res) => {
                  setValue('MediaGroupBackDrop', res);
                  trigger('MediaGroupBackDrop');
                }}
              />
            </Box>

            <Box
              sx={{
                mt: 3,
                display: 'grid',
                columnGap: 2,
                rowGap: 3,
                gridTemplateColumns: { xs: 'repeat(1, 1fr)' },
              }}
            >
              <RHFTextField name="MediaGroupBody" multiline rows={6} label="توضیحات زیر بنر" />
            </Box>

            <div className="flex items-center gap-4 mt-6">
              <LoadingButton type="submit" variant="contained" name="save" loading={isSubmitting}>
                ذخیره
              </LoadingButton>
              <LoadingButton type="submit" variant="contained" color="info" name="saveAndContinue" loading={isSubmitting}>
                {isEditMode ? 'ذخیره و ادامه ویرایش' : 'ذخیره و ایجاد بعدی'}
              </LoadingButton>
              <Button onClick={closeModal} variant="contained" color="error">
                انصراف
              </Button>
            </div>
          </Box>
        </Grid>
      </Grid>
    </FormProvider>
  );
};

export default MediaGroupForm;
